import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API } from "../lib/api";
import { injectJsonLd, SCHEMA_ORG } from "../lib/jsonld";
import Logo from "../components/Logo";
import { CalendarDots, ArrowLeft, CalendarBlank, Trophy, FacebookLogo, LinkedinLogo, XLogo, Link as LinkIcon, Check, Eye } from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function BlogPostDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = post?.title || "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié dans le presse-papiers");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
  };

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/blog/posts/${slug}`)
      .then(r => {
        setPost(r.data);
        const t = r.data.meta_title || `${r.data.title} | Institut Impact Élite Martial`;
        document.title = t;
        // Inject SEO meta tags
        upsertMeta("description", r.data.meta_description || r.data.excerpt || "");
        upsertMeta("og:title", r.data.meta_title || r.data.title, "property");
        upsertMeta("og:description", r.data.meta_description || r.data.excerpt || "", "property");
        if (r.data.featured_image_url) upsertMeta("og:image", r.data.featured_image_url, "property");
        upsertMeta("og:type", "article", "property");
        upsertCanonical(window.location.href);
        // Inject Schema.org BlogPosting JSON-LD
        const articleSchema = {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": r.data.title,
          "description": r.data.meta_description || r.data.excerpt || "",
          "datePublished": r.data.published_at || r.data.created_at,
          "dateModified": r.data.updated_at || r.data.published_at,
          "author": {
            "@type": "Person",
            "name": r.data.author || "Ralph David Jean-Pierre",
          },
          "publisher": {
            ...SCHEMA_ORG,
            "logo": { "@type": "ImageObject", "url": `${window.location.origin}/logo192.png` },
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": window.location.href },
          "articleSection": (r.data.category || "").replace("_", " "),
          "keywords": (r.data.tags || []).join(", "),
        };
        if (r.data.featured_image_url) articleSchema.image = r.data.featured_image_url;
        injectJsonLd("schema-article", articleSchema);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground ticker">Chargement…</div>;
  }
  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6">
        <div className="text-3xl font-bold">Article introuvable</div>
        <button onClick={() => navigate("/blog")} className="hard-border px-6 py-3 ticker hover:bg-[#D90429] hover:text-white hover:border-[#D90429]" data-testid="blog-back-to-list">Retour au blog</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="hard-border-b sticky top-0 z-30 bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="blog-detail-home"><Logo variant="static" tone="dark" /></Link>
          <nav className="flex items-center gap-6 ticker">
            <Link to="/" className="hover:text-[#D90429]">Accueil</Link>
            <Link to="/circuit" className="hover:text-[#D90429]">Circuit</Link>
            <Link to="/blog" className="text-[#D90429] font-semibold">Blog</Link>
            <Link to="/auth" className="hard-border px-4 py-2 hover:bg-[#D90429] hover:text-white hover:border-[#D90429]">Connexion</Link>
          </nav>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="inline-flex items-center gap-2 ticker text-muted-foreground hover:text-[#D90429] mb-8" data-testid="blog-back">
          <ArrowLeft size={14} /> Retour au blog
        </Link>

        <div className="ticker text-[#D90429] mb-4">{post.category.replace("_", " ").toUpperCase()}</div>
        <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-6 leading-tight" data-testid="blog-post-title">{post.title}</h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-10 hard-border-b pb-6">
          <div className="flex items-center gap-2">
            <CalendarDots size={14} />
            {formatDate(post.published_at)}
          </div>
          <div>·</div>
          <div>par <span className="font-semibold text-foreground">{post.author}</span></div>
          {(post.views_count || 0) > 0 && (
            <>
              <div>·</div>
              <div className="flex items-center gap-1.5" data-testid="blog-post-views" title={`${post.views_count} vue${post.views_count > 1 ? "s" : ""}`}>
                <Eye size={14} />
                {post.views_count}
              </div>
            </>
          )}
        </div>

        {post.featured_image_url && (
          <div className="mb-10 hard-border overflow-hidden" data-testid="blog-post-hero-image">
            <img src={post.featured_image_url} alt={post.title} className="w-full" />
          </div>
        )}

        <div className="prose prose-neutral dark:prose-invert max-w-none blog-content" data-testid="blog-post-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2" data-testid="blog-post-tags">
            {post.tags.map(t => (
              <span key={t} className="ticker px-3 py-1 hard-border">#{t}</span>
            ))}
          </div>
        )}

        {/* Share */}
        <div className="mt-12 hard-border-t pt-8" data-testid="blog-post-share">
          <div className="ticker text-muted-foreground mb-4">PARTAGER L'ARTICLE</div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="blog-share-facebook"
              className="hard-border px-4 py-3 ticker flex items-center gap-2 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-colors"
              aria-label="Partager sur Facebook"
            >
              <FacebookLogo size={18} weight="fill" /> Facebook
            </a>
            <a
              href={shareLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="blog-share-linkedin"
              className="hard-border px-4 py-3 ticker flex items-center gap-2 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors"
              aria-label="Partager sur LinkedIn"
            >
              <LinkedinLogo size={18} weight="fill" /> LinkedIn
            </a>
            <a
              href={shareLinks.x}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="blog-share-x"
              className="hard-border px-4 py-3 ticker flex items-center gap-2 hover:bg-black hover:text-white hover:border-black transition-colors"
              aria-label="Partager sur X"
            >
              <XLogo size={18} weight="fill" /> X
            </a>
            <button
              onClick={copyLink}
              data-testid="blog-share-copy"
              className="hard-border px-4 py-3 ticker flex items-center gap-2 hover:bg-[#D90429] hover:text-white hover:border-[#D90429] transition-colors"
              aria-label="Copier le lien"
            >
              {copied ? <><Check size={18} weight="bold" /> Copié</> : <><LinkIcon size={18} /> Copier le lien</>}
            </button>
          </div>
        </div>

        {post.related && post.related.length > 0 && (
          <div className="mt-12 hard-border-t pt-8" data-testid="blog-post-related">
            <div className="ticker text-muted-foreground mb-4">LIENS RELATIFS</div>
            <div className="space-y-3">
              {post.related.map((r, i) => (
                <div key={i} className="hard-border p-4 flex items-center gap-3">
                  {r.type === "event" ? <CalendarBlank size={20} className="text-[#D90429]" /> : <Trophy size={20} className="text-[#D90429]" />}
                  <div>
                    <div className="font-semibold">{r.name || r.full_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.type === "event" ? `${formatDate(r.date)} · ${r.location || ""}` : `${r.sport || ""} · ${r.level || ""}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>

      <footer className="hard-border-t mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center ticker text-muted-foreground">
          INSTITUT IMPACT ÉLITE MARTIAL · VAL-D'OR, QC
        </div>
      </footer>
    </div>
  );
}

function upsertMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
